 
import { Card, CardBody } from '@components/card/card';
import { getPagedData, setFilters, setPagination, setSortOrders } from '@store/DynamicSlice/dynamicTableSlice';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import React, { useState, useEffect } from 'react';
import { Offcanvas } from 'react-bootstrap';

interface FieldDefinition {
  name: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'boolean';
}

interface EnhancedDynamicFilterProps {
  fields: FieldDefinition[];
  onClose?: () => void;

  show: boolean;
}

interface FilterCondition {
  id: string;
  field: string;
  operator: number;
  value: string;
}

interface SortCondition {
  id: string;
  field: string;
  descending: boolean;
}

const operators = [
  { id: 0, label: 'يساوي', types: ['string', 'number', 'date', 'boolean'] },
  { id: 1, label: 'لا يساوي', types: ['string', 'number', 'date', 'boolean'] },
  { id: 2, label: 'أكبر من', types: ['number', 'date'] },
  { id: 3, label: 'أكبر من أو يساوي', types: ['number', 'date'] },
  { id: 4, label: 'أصغر من', types: ['number', 'date'] },
  { id: 5, label: 'أصغر من أو يساوي', types: ['number', 'date'] },
  { id: 6, label: 'يحتوي على', types: ['string'] },
  { id: 7, label: 'يبدأ بـ', types: ['string'] },
  { id: 8, label: 'ينتهي بـ', types: ['string'] }
];

export const DynamicFilter: React.FC<EnhancedDynamicFilterProps> = ({ fields, show, onClose }) => {
  const dispatch = useAppDispatch();
  const { pageIndex, pageSize, totalItems, filters, sortOrders } = useAppSelector(s => s.dynamicTable);

  const [filtersList, setFiltersList] = useState<FilterCondition[]>([]);
  const [sortsList, setSortsList] = useState<SortCondition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'filters' | 'sorting'>('filters');

  // استعادة الحالة من الـ store
  useEffect(() => {
    if (filters.length > 0) {
      setFiltersList(
        filters.map(
          (filter, index) =>
            ({
              id: `filter-${index}`,
              ...filter
            } as FilterCondition)
        )
      );
    } else {
      setFiltersList([]);
    }

    if (sortOrders.length > 0) {
      setSortsList(
        sortOrders.map(
          (sort, index) =>
            ({
              id: `sort-${index}`,
              ...sort
            } as SortCondition)
        )
      );
    } else {
      setSortsList([]);
    }
  }, [filters, sortOrders]);

  const generateId = () => `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const addFilter = () => {
    setFiltersList([
      ...filtersList,
      {
        id: generateId(),
        field: fields[0]?.name || '',
        operator: 0,
        value: ''
      }
    ]);
  };

  const removeFilter = (id: string) => {
    setFiltersList(filtersList.filter(filter => filter.id !== id));
  };

  const updateFilter = (id: string, updates: Partial<FilterCondition>) => {
    setFiltersList(filtersList.map(filter => (filter.id === id ? { ...filter, ...updates } : filter)));
  };

  const addSort = () => {
    setSortsList([
      ...sortsList,
      {
        id: generateId(),
        field: fields[0]?.name || '',
        descending: false
      }
    ]);
  };

  const removeSort = (id: string) => {
    setSortsList(sortsList.filter(sort => sort.id !== id));
  };

  const updateSort = (id: string, updates: Partial<SortCondition>) => {
    setSortsList(sortsList.map(sort => (sort.id === id ? { ...sort, ...updates } : sort)));
  };

  const handleApplyAll = async () => {
    setIsLoading(true);

    try {
      const activeFilters = filtersList.filter(filter => filter.field && filter.value !== '');
      const activeSorts = sortsList.filter(sort => sort.field);

      dispatch(setFilters(activeFilters));
      dispatch(setSortOrders(activeSorts));
      await dispatch(getPagedData());
      // onClose(); // إغلاق الـ offcanvas بعد التطبيق
      if (onClose) onClose();
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearAll = () => {
    setFiltersList([]);
    setSortsList([]);
    dispatch(setFilters([]));
    dispatch(setSortOrders([]));
    dispatch(getPagedData());
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setPagination({ pageIndex: newPage, pageSize }));
    dispatch(getPagedData());
  };

  const renderValueInput = (filter: FilterCondition) => {
    const selectedField = fields.find(f => f.name === filter.field);

    if (!selectedField)
      return <input type='text' placeholder='اختر حقل أولاً' disabled className='form-control form-control-sm' />;

    switch (selectedField.type) {
      case 'number':
        return (
          <input
            type='number'
            value={filter.value}
            onChange={e => updateFilter(filter.id, { value: e.target.value })}
            className='form-control form-control-sm'
            placeholder='أدخل رقماً'
          />
        );
      case 'date':
        return (
          <input
            type='date'
            value={filter.value}
            onChange={e => updateFilter(filter.id, { value: e.target.value })}
            className='form-control form-control-sm'
          />
        );
      case 'boolean':
        return (
          <select
            value={filter.value}
            onChange={e => updateFilter(filter.id, { value: e.target.value })}
            className='form-select form-select-sm'
          >
            <option value=''>اختر</option>
            <option value='true'>نعم</option>
            <option value='false'>لا</option>
          </select>
        );
      default:
        return (
          <input
            type='text'
            value={filter.value}
            onChange={e => updateFilter(filter.id, { value: e.target.value })}
            className='form-control form-control-sm'
            placeholder='أدخل نصاً'
          />
        );
    }
  };

  const getAvailableOperators = (fieldName: string) => {
    const selectedField = fields.find(f => f.name === fieldName);
    return selectedField ? operators.filter(op => op.types.includes(selectedField.type)) : operators;
  };

  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const hasActiveFilters =
    filtersList.some(filter => filter.field && filter.value !== '') || sortsList.some(sort => sort.field);

  return (
    <>
      <Offcanvas show={show} onHide={onClose} placement='end' style={{ width: '400px' }} className='shadow-lg'>
        <Offcanvas.Header closeButton className='border-bottom'>
          <Offcanvas.Title className='w-100'>
            <div className='d-flex align-items-center'>
              <i className='bi bi-funnel me-2 text-primary'></i>
              <h5 className='mb-0'>فلاتر البحث والترتيب</h5>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className='p-0'>
          <div className='h-100 d-flex flex-column'>
            {/* تبويبات التنقل */}
            <div className='border-bottom'>
              <div className='nav nav-pills nav-justified p-3'>
                <button
                  className={`nav-link btn-sm fw-semibold ${
                    activeTab === 'filters' ? 'active bg-primary text-white' : ''
                  }`}
                  onClick={() => setActiveTab('filters')}
                >
                  <i className='bi bi-funnel me-2'></i>
                  فلاتر البحث
                </button>
                <button
                  className={`nav-link btn-sm fw-semibold ${
                    activeTab === 'sorting' ? 'active bg-success text-white' : ''
                  }`}
                  onClick={() => setActiveTab('sorting')}
                >
                  <i className='bi bi-sort-down me-2'></i>
                  ترتيب النتائج
                </button>
              </div>
            </div>

            {/* محتوى التبويبات */}
            <div className='flex-grow-1' style={{ overflowY: 'auto' }}>
              {activeTab === 'filters' && (
                <div className='p-3'>
                  {/* رأس الفلاتر */}
                  <div className='d-flex align-items-center justify-content-between mb-3'>
                    <h6 className='mb-0 fw-semibold '>
                      <i className='bi bi-sliders me-2 text-primary'></i>
                      إعدادات الفلاتر
                    </h6>
                    <button onClick={addFilter} className='btn btn-primary btn-sm d-flex align-items-center gap-1'>
                      <i className='bi bi-plus-lg'></i>
                      إضافة فلتر
                    </button>
                  </div>

                  {/* قائمة الفلاتر */}
                  <div className='d-flex flex-column gap-2'>
                    {filtersList.length === 0 ? (
                      <div className='text-center py-4 px-3'>
                        <div
                          className='bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3'
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className='bi bi-funnel fs-4 text-primary'></i>
                        </div>
                        <h6 className='fw-semibold  mb-2'>لا توجد فلاتر مضافة</h6>
                        <p className='text-muted small mb-0'>انقر على "إضافة فلتر" لبدء إنشاء شروط البحث</p>
                      </div>
                    ) : (
                      filtersList.map((filter, index) => (
                        <div key={filter.id}>
                          <Card className=' shadow-sm'>
                            <CardBody className='p-3'>
                              {/* الصف الأول: الحقل وزر الحذف */}
                              <div className='row align-items-center g-2 mb-2'>
                                <div className='col-10'>
                                  <div className='d-flex align-items-center gap-2'>
                                    {/* رقم الفلتر */}
                                    <span
                                      className='badge bg-primary rounded-0 d-flex align-items-center justify-content-center flex-shrink-0'
                                      style={{ width: '22px', height: '22px', fontSize: '0.7rem' }}
                                    >
                                      {index + 1}
                                    </span>

                                    {/* الحقل */}
                                    <select
                                      value={filter.field}
                                      onChange={e =>
                                        updateFilter(filter.id, {
                                          field: e.target.value,
                                          value: '',
                                          operator: 0
                                        })
                                      }
                                      className='form-select form-select-sm border-secondary flex-grow-1'
                                    >
                                      <option value=''>اختر الحقل...</option>
                                      {fields.map(f => (
                                        <option key={f.name} value={f.name}>
                                          {f.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                <div className='col-2'>
                                  <button
                                    onClick={() => removeFilter(filter.id)}
                                    className='btn btn-outline-danger btn-sm w-100 d-flex align-items-center justify-content-center'
                                    style={{ height: '31px' }}
                                    title='حذف الفلتر'
                                  >
                                    <i className='bi bi-trash fa-lg'></i>
                                  </button>
                                </div>
                              </div>

                              {/* الصف الثاني: المقارنة والقيمة */}
                              <div className='row g-2 align-items-center'>
                                <div className='col-6'>
                                  <select
                                    value={filter.operator}
                                    onChange={e => updateFilter(filter.id, { operator: Number(e.target.value) })}
                                    className='form-select form-select-sm border-secondary'
                                    disabled={!filter.field}
                                  >
                                    {getAvailableOperators(filter.field).map(op => (
                                      <option key={op.id} value={op.id}>
                                        {op.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className='col-6'>{renderValueInput(filter)}</div>
                              </div>
                            </CardBody>
                          </Card>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'sorting' && (
                <div className='p-3'>
                  {/* رأس الترتيب */}
                  <div className='d-flex align-items-center justify-content-between mb-3'>
                    <h6 className='mb-0 fw-semibold '>
                      <i className='bi bi-arrow-down-up me-2 text-success'></i>
                      إعدادات الترتيب
                    </h6>
                    <button onClick={addSort} className='btn btn-success btn-sm d-flex align-items-center gap-1'>
                      <i className='bi bi-plus-lg'></i>
                      إضافة ترتيب
                    </button>
                  </div>

                  {/* قائمة الترتيب */}
                  <div className='d-flex flex-column gap-3'>
                    {sortsList.length === 0 ? (
                      <div className='text-center py-5 text-muted border border-2 border-dashed rounded-3'>
                        <i className='bi bi-sort-down d-block mb-2 fs-2 text-success opacity-50'></i>
                        <span className='fw-medium'>لا توجد ترتيبات مضافة</span>
                        <br />
                        <small className='text-muted'>انقر على "إضافة ترتيب" لبدء إضافة شروط الترتيب</small>
                      </div>
                    ) : (
                      sortsList.map((sort, index) => (
                        <div key={sort.id} className='border rounded-3 p-3 shadow-sm'>
                          <div className='d-flex align-items-start gap-3'>
                            <div className='d-flex flex-column align-items-center'>
                              <span
                                className='badge bg-success rounded-circle d-flex align-items-center justify-content-center'
                                style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}
                              >
                                {index + 1}
                              </span>
                            </div>

                            <div className='flex-grow-1'>
                              <div className='row g-3'>
                                <div className='col-8'>
                                  <label className='form-label small fw-semibold text-muted mb-2'>حقل الترتيب</label>
                                  <select
                                    value={sort.field}
                                    onChange={e => updateSort(sort.id, { field: e.target.value })}
                                    className='form-select form-select-sm border'
                                  >
                                    <option value=''>اختر حقل الترتيب...</option>
                                    {fields.map(f => (
                                      <option key={f.name} value={f.name}>
                                        {f.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className='col-4'>
                                  <label className='form-label small fw-semibold text-muted mb-2'>الاتجاه</label>
                                  <select
                                    value={sort.descending ? 'desc' : 'asc'}
                                    onChange={e => updateSort(sort.id, { descending: e.target.value === 'desc' })}
                                    className='form-select form-select-sm border'
                                    disabled={!sort.field}
                                  >
                                    <option value='asc'>↗ تصاعدي</option>
                                    <option value='desc'>↘ تنازلي</option>
                                  </select>
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={() => removeSort(sort.id)}
                              className='btn btn-outline-danger btn-sm p-2 d-flex align-items-center justify-content-center'
                              style={{ width: '32px', height: '32px' }}
                              title='حذف الترتيب'
                            >
                              <i className='bi bi-x' style={{ fontSize: '0.8rem' }}></i>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* قسم التحكم السفلي */}
            <div className='border-top p-3 bg-secondary bg-opacity-50'>
              <div className='d-flex flex-column gap-3'>
                {/* الباجينيشن */}
                <div className='d-flex align-items-center justify-content-between p-2 bg-body rounded-2 border'>
                  <div className='d-flex align-items-center gap-1'>
                    <i className='bi bi-list-ul text-muted'></i>
                    <span className='fw-bold'>{totalItems.toLocaleString()}</span>
                    <span className='small text-muted'>عنصر</span>
                  </div>

                  <div className='d-flex align-items-center gap-1'>
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pageIndex === 1}
                      className='btn btn-outline-primary btn-sm p-1'
                      style={{ width: '28px', height: '28px' }}
                      title='الأولى'
                    >
                      <i className='bi bi-chevron-double-left' style={{ fontSize: '0.7rem' }}></i>
                    </button>

                    <button
                      onClick={() => handlePageChange(Math.max(1, pageIndex - 1))}
                      disabled={pageIndex === 1}
                      className='btn btn-outline-primary btn-sm p-1'
                      style={{ width: '28px', height: '28px' }}
                      title='السابقة'
                    >
                      <i className='bi bi-chevron-left' style={{ fontSize: '0.7rem' }}></i>
                    </button>

                    <div className='mx-2 text-center'>
                      <div className='fw-bold ' style={{ fontSize: '0.9rem' }}>
                        {pageIndex}
                      </div>
                      <div className='small text-muted' style={{ fontSize: '0.7rem' }}>
                        من {totalPages}
                      </div>
                    </div>

                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, pageIndex + 1))}
                      disabled={pageIndex >= totalPages}
                      className='btn btn-outline-primary btn-sm p-1'
                      style={{ width: '28px', height: '28px' }}
                      title='التالية'
                    >
                      <i className='bi bi-chevron-right' style={{ fontSize: '0.7rem' }}></i>
                    </button>

                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={pageIndex >= totalPages}
                      className='btn btn-outline-primary btn-sm p-1'
                      style={{ width: '28px', height: '28px' }}
                      title='الأخيرة'
                    >
                      <i className='bi bi-chevron-double-right' style={{ fontSize: '0.7rem' }}></i>
                    </button>
                  </div>

                  <div className='d-flex align-items-center gap-1'>
                    <i className='bi bi-file-text text-muted'></i>
                    <span className='fw-bold '>{pageIndex}</span>
                    <span className='small text-muted'>صفحة</span>
                  </div>
                </div>

                {/* حالة الفلاتر النشطة */}
                {hasActiveFilters && (
                  <div className='d-flex flex-column gap-2'>
                    <div className='small fw-semibold text-muted'>الفلاتر النشطة:</div>
                    <div className='d-flex gap-2 flex-wrap'>
                      {filtersList
                        .filter(f => f.field && f.value !== '')
                        .slice(0, 3)
                        .map(filter => {
                          const field = fields.find(f => f.name === filter.field);
                          const operator = operators.find(op => op.id === filter.operator);
                          return (
                            <span
                              key={filter.id}
                              className='badge bg-theme bg-opacity-10 text-theme border border-theme border-opacity-25 d-flex align-items-center gap-1'
                            >
                              <i className='bi bi-funnel'></i>
                              {field?.label} {operator?.label} {filter.value}
                            </span>
                          );
                        })}
                      {sortsList
                        .filter(s => s.field)
                        .slice(0, 2)
                        .map(sort => {
                          const field = fields.find(f => f.name === sort.field);
                          return (
                            <span
                              key={sort.id}
                              className='badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 d-flex align-items-center gap-1'
                            >
                              <i className='bi bi-sort-down'></i>
                              {field?.label} {sort.descending ? '↓' : '↑'}
                            </span>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* أزرار الإجراءات */}
                <div className='d-flex gap-2'>
                  <button
                    onClick={handleClearAll}
                    className='btn btn-outline-secondary flex-grow-1 d-flex align-items-center justify-content-center gap-2'
                    disabled={!hasActiveFilters}
                  >
                    <i className='bi bi-arrow-clockwise'></i>
                    إعادة التعيين
                  </button>

                  <button
                    onClick={handleApplyAll}
                    className='btn btn-primary flex-grow-1 d-flex align-items-center justify-content-center gap-2'
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className='spinner-border spinner-border-sm'></span>
                        جاري التطبيق...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-check-lg'></i>
                        تطبيق الفلاتر
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <style>{`
        .offcanvas-backdrop.show {
    opacity: 0.0;
}
        `}</style>
    </>
  );
};
